// ABOUTME: Pages a jf-paged-carousel: dots and arrows scroll the card row a page at a time (a set number of cards, the last page
// ending on the last card, one card per page below 768px), and scrolling by swipe or trackpad moves the active dot.
class JfPagedCarousel extends HTMLElement {
  #track = null;
  #mobile = window.matchMedia('(max-width: 767px)');
  /* The 768-1199px tier has its own peek and can wrap the cards as a grid, so crossing it re-reads the pages. */
  #tablet = window.matchMedia('(min-width: 768px) and (max-width: 1199px)');
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector('.jf-paged-carousel__track');
    if (!this.#track) return;
    this.addEventListener('click', this.#onClick);
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#mobile.addEventListener('change', this.#update);
    this.#tablet.addEventListener('change', this.#update);
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#mobile.removeEventListener('change', this.#update);
    this.#tablet.removeEventListener('change', this.#update);
    cancelAnimationFrame(this.#frame);
  }

  get #step() {
    return this.#mobile.matches ? 1 : Number(this.dataset.step) || 1;
  }

  /** Scroll offset of each page: page p starts at card p * step, and the last page is the end of the row; the track's scroll
   * snapping then settles a page half a peek earlier, as PageFly's slider does. Only rendered children count as cards: a card with
   * its own background colour brings a <style> element into the row. */
  #offsets() {
    const slides = [...this.#track.children].filter((child) => child.getClientRects().length > 0);
    const max = this.#track.scrollWidth - this.#track.clientWidth;
    if (!slides.length || max <= 0) return [0];
    const base = slides[0].offsetLeft;
    const offsets = [];
    for (let i = 0; i < slides.length; i += this.#step) {
      const left = Math.min(slides[i].offsetLeft - base, max);
      offsets.push(left);
      if (left >= max - 1) break;
    }
    return offsets;
  }

  #currentPage() {
    const offsets = this.#offsets();
    const x = this.#track.scrollLeft;
    return offsets.reduce((best, offset, i) => (Math.abs(offset - x) < Math.abs(offsets[best] - x) ? i : best), 0);
  }

  #goTo(page) {
    const offsets = this.#offsets();
    const target = Math.max(0, Math.min(page, offsets.length - 1));
    this.#track.scrollTo({ left: offsets[target], behavior: 'smooth' });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    const dot = target.closest('.jf-paged-carousel__dot');
    if (dot instanceof HTMLElement) {
      this.#goTo(Number(dot.dataset.page));
      return;
    }
    const arrow = target.closest('.jf-paged-carousel__arrow');
    if (arrow instanceof HTMLElement) this.#goTo(this.#currentPage() + Number(arrow.dataset.dir));
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    const page = this.#currentPage();
    const last = this.#offsets().length - 1;
    const set = this.#mobile.matches ? '--mobile' : '--desktop';
    this.querySelectorAll(`.jf-paged-carousel__dots${set} .jf-paged-carousel__dot`).forEach((dot, i) => {
      dot.setAttribute('aria-current', i === page ? 'true' : 'false');
    });
    const prev = this.querySelector('.jf-paged-carousel__arrow--prev');
    const next = this.querySelector('.jf-paged-carousel__arrow--next');
    if (prev instanceof HTMLElement) prev.hidden = page === 0;
    if (next instanceof HTMLElement) next.hidden = page >= last;
  };
}

if (!customElements.get('jf-paged-carousel')) {
  customElements.define('jf-paged-carousel', JfPagedCarousel);
}
